import { contrastRatio, mixHex, pickInk, towardsContrast } from "./color";

// Foreground roles derived per surface, @adr SPARK-0053-the-signal-colour-stays-unchanged@2.
// WCAG 2.2 AA: 4.5:1 for text (1.4.3), 3:1 for the non-text cues a control
// needs to be recognised: boundaries and keyboard focus (1.4.11). The
// margin keeps a value that lands exactly on the line after hex rounding off it.
export const TEXT_CONTRAST = 4.5;
export const UI_CONTRAST = 3;
export const MARGIN = 0.05;
// Disabled controls are exempt from both criteria. 2:1 keeps them visibly
// present on every palette while reading clearly as unavailable.
const DISABLED_CONTRAST = 2;

// The washes text may sit on inside a surface: up to 10% ink (chip rest,
// hover row, empty-state wash) and up to 10% accent (accent chips, active
// power tile). Muted, ui, disabled and accent are checked against the fill AND
// these, so a label on a hovered row or an active tile keeps its contrast.
// Stronger washes (the 40% modal scrim) carry no text.
export const INK_WASH = 0.1;
export const ACCENT_WASH = 0.1;
// How far a button's fill shifts on hover.
export const HOVER_SHIFT = 0.12;
// The fixed "needs a look" status hue (amber). Like danger it is not
// themable; unlike a fill it is mostly used as text or a dot, so its
// foreground form still shifts towards ink to meet the text target.
export const ATTENTION = "#d97706";

export interface Palette {
  page: string;
  surface: string;
  accent: string;
}

export const DEFAULT_PALETTE: Palette = { page: "#ffffff", surface: "#faf9f6", accent: "#000000" };

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

// Palette values arrive from SystemSettings and localStorage unchecked. The
// contrast maths only understands #rgb / #rrggbb, while a browser would happily
// paint "white" or "#ffffff80": the computed ink and the painted fill would
// then disagree (white text on a white page). Anything else falls back to the
// role's default, so what is painted is always what was computed.
export function normalizeHex(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const match = HEX.exec(value.trim());
  if (!match) return fallback;
  const hex = match[1]!.toLowerCase();
  return "#" + (hex.length === 3 ? [...hex].map((c) => c + c).join("") : hex);
}

export function normalizePalette(palette: { [K in keyof Palette]?: unknown }): Palette {
  return {
    page: normalizeHex(palette.page, DEFAULT_PALETTE.page),
    surface: normalizeHex(palette.surface, DEFAULT_PALETTE.surface),
    accent: normalizeHex(palette.accent, DEFAULT_PALETTE.accent),
  };
}

export const SURFACE_ROLES = ["page", "surface", "accent"] as const;
export type SurfaceRole = (typeof SURFACE_ROLES)[number];

// Everything that may sit on one fill, each value checked against that fill.
export interface FillTokens {
  fill: string;
  ink: string;
  inkMuted: string;
  inkUi: string;
  inkDisabled: string;
  accent: string;
  accentUi: string;
  accentTintInk: string;
  accentTintText: string;
  attention: string;
  scheme: "light" | "dark";
}

export interface RoleTokens extends FillTokens {
  inverse: FillTokens & { hover: string };
}

export function washesOf(fill: string, ink: string, accent: string): string[] {
  return [
    fill,
    mixHex(fill, ink, INK_WASH),
    mixHex(fill, accent, ACCENT_WASH),
    mixHex(fill, ATTENTION, ACCENT_WASH),
  ];
}

function tokensOn(fill: string, accent: string, ink: string): FillTokens {
  const backgrounds = washesOf(fill, ink, accent);
  const tint = mixHex(fill, accent, 0.15);
  const tintInk = pickInk(tint);
  const inkUi = towardsContrast(fill, ink, backgrounds, UI_CONTRAST + MARGIN);
  return {
    fill,
    ink,
    inkMuted: towardsContrast(fill, ink, backgrounds, TEXT_CONTRAST + MARGIN),
    inkUi,
    inkDisabled: towardsContrast(fill, ink, backgrounds, DISABLED_CONTRAST),
    accent: backgrounds.every((bg) => contrastRatio(accent, bg) >= TEXT_CONTRAST + MARGIN) ? accent : ink,
    accentUi: backgrounds.every((bg) => contrastRatio(accent, bg) >= UI_CONTRAST + MARGIN) ? accent : inkUi,
    accentTintInk: tintInk,
    accentTintText: contrastRatio(accent, tint) >= TEXT_CONTRAST + MARGIN ? accent : tintInk,
    attention: towardsContrast(ATTENTION, ink, backgrounds, TEXT_CONTRAST + MARGIN),
    scheme: pickInk(fill) === "#000000" ? "light" : "dark",
  };
}

// The inverse fill of a role is the strongest palette colour that reads on it:
// the page colour where it contrasts with the role's fill (a navy page makes
// navy buttons on cream cards), black or white otherwise. Its text is the
// role's own fill, which by construction meets the text contrast on it.
function inverseFill(role: SurfaceRole, fill: string, palette: Palette): string {
  if (role !== "page" && contrastRatio(palette.page, fill) >= TEXT_CONTRAST + MARGIN) {
    return palette.page;
  }
  return pickInk(fill);
}

// A visible hover shift that keeps the label readable: towards the label where
// the contrast allows it, otherwise away from it (which only ever raises the
// contrast). Black stays black when shifted further away, so that case moves
// towards the label as far as the norm permits instead.
function hoverFill(fill: string, label: string): string {
  const towards = mixHex(fill, label, HOVER_SHIFT);
  if (contrastRatio(towards, label) >= TEXT_CONTRAST + MARGIN) return towards;
  const away = mixHex(fill, pickInk(label) === "#000000" ? "#000000" : "#ffffff", HOVER_SHIFT);
  if (away !== fill) return away;
  return towardsContrast(towards, fill, label, TEXT_CONTRAST + MARGIN);
}

export function deriveRoleTokens(palette: Palette): Record<SurfaceRole, RoleTokens> {
  const result = {} as Record<SurfaceRole, RoleTokens>;
  for (const role of SURFACE_ROLES) {
    const fill = palette[role];
    const own = tokensOn(fill, palette.accent, pickInk(fill));
    const inverse = tokensOn(inverseFill(role, fill, palette), palette.accent, fill);
    result[role] = { ...own, inverse: { ...inverse, hover: hoverFill(inverse.fill, inverse.ink) } };
  }
  return result;
}

// Flattened into the custom properties index.css binds per surface class.
export function themeCustomProperties(input: { [K in keyof Palette]?: unknown }): Record<string, string> {
  const props: Record<string, string> = {};
  const roles = deriveRoleTokens(normalizePalette(input));
  for (const role of SURFACE_ROLES) {
    const write = (prefix: string, t: FillTokens) => {
      props[`--color-${prefix}`] = t.fill;
      props[`--color-on-${prefix}`] = t.ink;
      props[`--color-on-${prefix}-muted`] = t.inkMuted;
      props[`--color-on-${prefix}-ui`] = t.inkUi;
      props[`--color-on-${prefix}-disabled`] = t.inkDisabled;
      props[`--color-accent-on-${prefix}`] = t.accent;
      props[`--color-accent-ui-on-${prefix}`] = t.accentUi;
      props[`--color-accent-tint-ink-on-${prefix}`] = t.accentTintInk;
      props[`--color-accent-tint-text-on-${prefix}`] = t.accentTintText;
      props[`--color-attention-on-${prefix}`] = t.attention;
      props[`--color-${prefix}-scheme`] = t.scheme;
    };
    write(role, roles[role]);
    write(`${role}-inverse`, roles[role].inverse);
    props[`--color-${role}-inverse-hover`] = roles[role].inverse.hover;
  }
  return props;
}
